// ── Chart annotation layer: layout (Iteration 4 §4) ─────────────────────────
// Annotations are pills, not loose SVG text: a rounded rect in the kind's fill
// with a title line (11.5 / 700) and a date line (10.5 mono). After placement
// they are sorted by x and stacked so no two overlap; a pill that would leave
// the plot flips to the other side of its line. Colours come from the
// `--rd-pill-*` tokens so light theme reads its own values.

export type PillKind = 'csa' | 'projection' | 'scope' | 'version'

export const PILL_VARS: Record<PillKind, { bg: string; fg: string }> = {
  csa: { bg: 'var(--rd-pill-csa-bg)', fg: 'var(--rd-pill-csa-fg)' },
  projection: { bg: 'var(--rd-pill-projection-bg)', fg: 'var(--rd-pill-projection-fg)' },
  scope: { bg: 'var(--rd-pill-scope-bg)', fg: 'var(--rd-pill-scope-fg)' },
  version: { bg: 'var(--rd-pill-version-bg)', fg: 'var(--rd-pill-version-fg)' },
}

export const PAD_X = 8
export const PAD_Y = 6
export const TITLE = 11.5
export const DATE = 10.5
export const LINE_GAP = 3
export const SANS = "'Work Sans', system-ui, sans-serif"
export const MONO = "'JetBrains Mono', ui-monospace, monospace"

let canvas: CanvasRenderingContext2D | null | undefined
function textWidth(text: string, font: string, fallbackPerChar: number) {
  if (canvas === undefined) canvas = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null
  if (!canvas) return text.length * fallbackPerChar
  canvas.font = font
  return canvas.measureText(text).width
}

export interface PillSpec {
  key: string
  kind: PillKind
  title: string
  date?: string | null
  /** Full text for the hover tooltip; nothing is removed from it at narrow widths. */
  full?: string
  /** The line or marker the pill belongs to. */
  ax: number
  ay: number
  /** Which side of the anchor the pill sits on; flips at the plot edge. */
  side: 'right' | 'left'
  testId?: string
}

export interface PlacedPill extends PillSpec {
  x: number
  y: number
  w: number
  h: number
}

export function pillSize(title: string, date?: string | null) {
  const tw = textWidth(title, `700 ${TITLE}px ${SANS}`, 6.7)
  const dw = date ? textWidth(date, `400 ${DATE}px ${MONO}`, 6.4) : 0
  return { w: Math.ceil(Math.max(tw, dw) + 2 * PAD_X), h: Math.ceil(2 * PAD_Y + TITLE + (date ? LINE_GAP + DATE : 0)) }
}

// Place, flip at the edges, then stack: sorted by x, a pill overlapping an
// earlier one by more than 4px moves down by its height + 4 (three stacks at
// most) and is clipped to the plot.
export function layoutPills(specs: PillSpec[], plot: { left: number; right: number; top: number; bottom: number }): PlacedPill[] {
  const GAP = 6
  const placed: PlacedPill[] = specs.map((s) => {
    const { w, h } = pillSize(s.title, s.date)
    let side = s.side
    let x = side === 'right' ? s.ax + GAP : s.ax - GAP - w
    if (side === 'right' && x + w > plot.right) { side = 'left'; x = s.ax - GAP - w }
    if (side === 'left' && x < plot.left) { side = 'right'; x = s.ax + GAP }
    x = Math.max(plot.left, Math.min(plot.right - w, x))
    return { ...s, side, x, y: s.ay, w, h }
  })
  placed.sort((a, b) => a.x - b.x)
  const done: PlacedPill[] = []
  placed.forEach((p) => {
    const clip = (y: number) => Math.max(plot.top, Math.min(plot.bottom - p.h, y))
    p.y = clip(p.y)
    for (let stack = 0; stack < 3; stack++) {
      const hit = done.find((q) => overlaps(p, q))
      if (!hit) break
      p.y = clip(hit.y + hit.h + 4)
    }
    done.push(p)
  })
  return done
}

const overlaps = (a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) =>
  Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) > 4 && Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y) > 0

export const coversPoint = (pills: PlacedPill[], x: number, y: number, pad = 8) => pills.some((p) => x >= p.x - pad && x <= p.x + p.w + pad && y >= p.y - pad && y <= p.y + p.h + pad)
